import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type InitializationState = 'idle' | 'checking' | 'downloading' | 'processing' | 'completed' | 'error'

export const usePotaParksInitStore = defineStore('potaParksInit', () => {
  // State
  const state = ref<InitializationState>('idle')
  const totalParks = ref(0)
  const processedParks = ref(0)
  const errorMessage = ref<string | null>(null)
  const isInitializing = ref(false)

  // Computed
  const isLoading = computed(() => 
    ['checking', 'downloading', 'processing'].includes(state.value)
  )
  
  const progressPercentage = computed(() => {
    if (totalParks.value === 0) return 0
    return Math.round((processedParks.value / totalParks.value) * 100)
  })

  const statusMessage = computed(() => {
    switch (state.value) {
      case 'checking':
        return 'Setting up parks database...'
      case 'downloading':
        return `Downloading ${totalParks.value.toLocaleString()} parks...`
      case 'processing':
        return `Setting up parks database (${processedParks.value.toLocaleString()} / ${totalParks.value.toLocaleString()})`
      case 'completed':
        return 'Parks database ready!'
      case 'error':
        return errorMessage.value || 'Failed to initialize parks database'
      default:
        return 'Setting up parks database...'
    }
  })

  // Actions
  function setState(newState: InitializationState) {
    state.value = newState
  }

  function setProgress(processed: number, total: number) {
    processedParks.value = processed
    totalParks.value = total
  }

  function setError(message: string) {
    state.value = 'error'
    errorMessage.value = message
    isInitializing.value = false
  }

  function setCompleted() {
    state.value = 'completed'
    isInitializing.value = false
  }

  function startInitialization() {
    // Guard against re-entrant initialization
    if (isInitializing.value || ['checking', 'downloading', 'processing'].includes(state.value)) {
      console.log('🏕️ [POTA PARKS] Initialization already in progress, skipping duplicate start')
      return
    }
    
    // Set initializing flag immediately to prevent race conditions
    isInitializing.value = true
    state.value = 'checking'
    errorMessage.value = null
    processedParks.value = 0
    totalParks.value = 0
  }

  function reset() {
    state.value = 'idle'
    totalParks.value = 0
    processedParks.value = 0
    errorMessage.value = null
    isInitializing.value = false
  }

  return {
    // State
    state,
    totalParks,
    processedParks,
    errorMessage,
    isInitializing,
    
    // Computed
    isLoading,
    progressPercentage,
    statusMessage,
    
    // Actions
    setState,
    setProgress,
    setError,
    setCompleted,
    startInitialization,
    reset
  }
})